import { useState, type ReactNode } from 'react';
import { FiChevronRight, FiEye, FiSlash, FiTrash2 } from 'react-icons/fi';
import { RowActionButton } from '@/shared/ui/data-table';
import { ConfirmDialog } from '@/shared/ui/confirm-dialog';
import { useNotification } from '@/shared/ui/notification';
import { isSuspendEdgeLegal } from '@/modules/tenants/services/api';
import { TenantApi } from '@/modules/tenants/services/api';
import type { Tenant } from '@/api/schemas/tenants';

/**
 * The actions rendered in the last column of the tenant list.
 *
 * WHY THE ACTIONS LIVE IN ONE COMPONENT
 * -------------------------------------
 * They share one piece of state — which confirmation is open — and they must
 * never both be open. Keeping them together makes that exclusivity structural:
 * there is one `confirming` value, so "suspend and delete are both showing" is
 * not a state this file can express.
 *
 * WHY DELETE IS DISABLED, AND SAYS SO
 * -----------------------------------
 * The register is explicit that hard delete is NOT an M01 behaviour:
 * `DELETE /api/v1/tenants/{id}` is recorded as **NOT APPLICABLE**, with the
 * reason that `M01-R02` exists precisely because suspension must not delete.
 * The `closed` status is TERMINAL and the `active -> closed` and
 * `suspended -> closed` edges are DEFERRED, not omitted.
 *
 * So there is no endpoint to call. A Delete button that fired would be a button
 * that 404s — the exact failure the register warns about. It is rendered
 * DISABLED, and its tooltip states the reason rather than leaving the operator
 * to guess. The action is visible so the surface is honest about what the
 * platform does not offer; it is inert so it cannot lie about what it does.
 *
 * WHY SUSPEND IS CONDITIONAL
 * --------------------------
 * `isSuspendEdgeLegal` mirrors the API's own guard. It is used only to avoid
 * OFFERING an action known to be refused — never to conclude one is permitted.
 * A `closed` tenant is terminal, so it gets no Suspend button at all rather
 * than a button that would be rejected.
 *
 * WHY THERE ARE TWO VARIANTS
 * --------------------------
 * `inline` is the icon-only row the table and the desktop card carry, where
 * the label lives in a tooltip. `sheet` is the same actions rendered as
 * full-width labelled rows inside the bottom sheet a card opens on a phone.
 * A touch screen cannot show a hover tooltip, so in the sheet the label has to
 * be visible text — and a full-width row is a far larger target than a 28px
 * square. The actions themselves are identical; only their dress differs, so
 * the two variants cannot drift apart in behaviour.
 */
export function TenantRowActions({
  row,
  variant = 'inline',
}: {
  row: Tenant;
  variant?: 'inline' | 'sheet';
}): ReactNode {
  const [confirming, setConfirming] = useState<'suspend' | null>(null);
  const [pending, setPending] = useState(false);
  const { notify } = useNotification();

  const canSuspend = isSuspendEdgeLegal(row.status, 'suspended');

  async function handleSuspend(): Promise<void> {
    setPending(true);
    try {
      await TenantApi.changeStatus(row.id, {
        status: 'suspended',
        reason: 'Suspended from the platform console',
      });
      setConfirming(null);
      notify({ title: `${row.slug} suspended`, tone: 'success' });
    } catch (error) {
      // The page does not restate the server's reason; a failed write is a
      // failed write, and the operator is told that rather than a guess.
      console.error(error);
      notify({ title: `Could not suspend ${row.slug}`, tone: 'error' });
    } finally {
      setPending(false);
    }
  }

  const openDetail = () => globalThis.location?.assign(`/platform/tenants/${row.id}`);

  return (
    <>
      {variant === 'sheet' ? (
        <SheetActions
          canSuspend={canSuspend}
          onView={openDetail}
          onSuspend={() => setConfirming('suspend')}
        />
      ) : (
        <InlineActions
          canSuspend={canSuspend}
          onView={openDetail}
          onSuspend={() => setConfirming('suspend')}
        />
      )}

      <ConfirmDialog
        open={confirming === 'suspend'}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
        title={`Suspend ${row.slug}?`}
        description="The tenant and everything it holds stay addressable. Suspension deletes nothing."
        confirmLabel="Suspend tenant"
        tone="danger"
        busy={pending}
        onConfirm={() => {
          void handleSuspend();
        }}
      />
    </>
  );
}

/** The labelled full-width rows the bottom sheet carries. */
function SheetActions({
  canSuspend,
  onView,
  onSuspend,
}: {
  canSuspend: boolean;
  onView: () => void;
  onSuspend: () => void;
}): ReactNode {
  return (
    <div className="flex flex-col gap-2">
      <SheetActionRow
        label="View tenant"
        tone="primary"
        icon={<FiEye size={16} aria-hidden="true" />}
        onClick={onView}
      />
      {canSuspend ? (
        <SheetActionRow
          label="Suspend tenant"
          tone="warning"
          icon={<FiSlash size={16} aria-hidden="true" />}
          onClick={onSuspend}
        />
      ) : null}
      <SheetActionRow
        label="Delete tenant"
        tone="danger"
        icon={<FiTrash2 size={16} aria-hidden="true" />}
        disabledReason="Hard delete is not an M01 behaviour — suspension must not delete."
      />
    </div>
  );
}

/** The icon-only row the table and the desktop card carry. */
function InlineActions({
  canSuspend,
  onView,
  onSuspend,
}: {
  canSuspend: boolean;
  onView: () => void;
  onSuspend: () => void;
}): ReactNode {
  return (
    <div className="flex items-center gap-1.5">
      <RowActionButton
        label="View tenant"
        icon={<FiEye size={14} aria-hidden="true" />}
        onClick={onView}
      />

      {canSuspend ? (
        <RowActionButton
          label="Suspend tenant"
          tone="warning"
          icon={<FiSlash size={14} aria-hidden="true" />}
          onClick={onSuspend}
        />
      ) : null}

      <RowActionButton
        label="Delete tenant"
        tone="danger"
        icon={<FiTrash2 size={14} aria-hidden="true" />}
        disabledReason="Hard delete is not an M01 behaviour — suspension must not delete."
      />
    </div>
  );
}

/**
 * One action inside the sheet, as a full-width labelled row.
 *
 * The label is visible text rather than a tooltip, because a touch screen has
 * no hover. A disabled row keeps its label and states its reason inline — the
 * sheet has the width for it, and a dimmed row with no explanation is the same
 * dead end the icon button's tooltip existed to avoid.
 *
 * WHY THE ROWS ARE TONED
 * ----------------------
 * Three identical white rows made the sheet a list of look-alikes: nothing
 * said which one was safe and which one was destructive, so the eye had to
 * read every label before it could act. Each row now carries its own tone —
 * View is the neutral/primary action, Suspend is caution, Delete is critical —
 * so the weight of an action is legible before its label is read. The tone is
 * carried by the icon and the border, not by the fill alone, so it survives a
 * colour-blind reader and a monochrome print.
 */
function SheetActionRow({
  label,
  icon,
  tone = 'neutral',
  onClick,
  disabledReason,
}: {
  label: string;
  icon: ReactNode;
  tone?: 'primary' | 'neutral' | 'warning' | 'danger';
  onClick?: () => void;
  disabledReason?: string;
}): ReactNode {
  const disabled = disabledReason !== undefined;
  return (
    <button
      type="button"
      className={sheetActionRowStyles[tone]}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-disabled={disabled}
    >
      <span className={sheetActionIconStyles[tone]}>{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block">{label}</span>
        {disabledReason ? (
          <span className="mt-0.5 block text-xs font-normal text-content-muted">
            {disabledReason}
          </span>
        ) : null}
      </span>
      {disabled ? null : (
        <FiChevronRight size={16} aria-hidden="true" className="shrink-0 text-content-subtle" />
      )}
    </button>
  );
}

/**
 * The row's dress, per tone.
 *
 * The base is shared so every row is the same size and shape; only the border,
 * the icon colour and the hover fill change. `bg-surface-raised` is kept as the
 * resting fill so a row still reads as a raised control rather than as a
 * coloured block, and the tone arrives on the border and the icon.
 */
const sheetActionRowBase =
  'flex w-full items-center gap-3 rounded-control border px-3 py-2.5 text-left text-sm font-medium text-content transition-colors disabled:cursor-not-allowed disabled:opacity-60';

const sheetActionRowStyles: Record<'primary' | 'neutral' | 'warning' | 'danger', string> = {
  primary: `${sheetActionRowBase} border-accent/40 bg-accent-soft hover:border-accent`,
  neutral: `${sheetActionRowBase} border-border bg-surface-raised hover:border-border-strong hover:bg-surface-sunken`,
  warning: `${sheetActionRowBase} border-caution/40 bg-caution-soft hover:border-caution`,
  danger: `${sheetActionRowBase} border-critical/40 bg-critical-soft hover:border-critical`,
};

const sheetActionIconStyles: Record<'primary' | 'neutral' | 'warning' | 'danger', string> = {
  primary: 'shrink-0 text-accent',
  neutral: 'shrink-0 text-content-muted',
  warning: 'shrink-0 text-caution',
  danger: 'shrink-0 text-critical',
};
